import React from 'react'
import type { PresentationPreview, PresentationStyle, QuoteFile, PresentationLibrary } from 'paintscout'
import type { Theme } from '@paintscout/ui/styles'
import type { TypographyStyleOptions } from '@paintscout/ui'
import type { Editor } from '@tiptap/react'
import type { TemplateTag } from '@paintscout/util/util'
import type { Context } from '@paintscout/util/templater'
// the module file, not `../PresentationNext/util` — that barrel pulls in components which import
// this file back. A type-only import is erased, so this can't become a runtime cycle either way.
import type { PresentationZoom } from '../PresentationNext/util/presentationZoom'

/**
 * A catalog tile being natively dragged out of the Add panel.
 *
 * `images` is the company's own photo list the *tile* rendered its preview with. The drop handler
 * reuses it instead of calling `getDefaultPresentationImages` again, because that function shuffles:
 * recomputing it would insert the same layout with the photos in a different order than the tile the
 * user dragged (#22688). Absent for block tiles, which preview as icons and have no photos to match.
 * Trusted — this is React state we set ourselves, not the attacker-controllable DataTransfer payload.
 */
export interface CatalogDrag {
  kind: 'block' | 'section'
  label: string
  images?: QuoteFile[]
}

// Value of our presentation context
export interface PresentationContextValue {
  /**
   * The key of whatever the sidebar tree (or a page tab) last selected — a page, section, or block
   * key. Drives the canvas scroll target and canvas focus. NOT the same as which page is showing:
   * a section/block key here still belongs to a page, so consumers that need "the page to render or
   * highlight" must resolve it with `resolvePageForKey` against the **live Formik tree** —
   * `useCurrentPageKey()` (`PresentationNext/util`) is that resolution, and the canvas does its own
   * via `Presentation`'s `activePage`.
   *
   * There is deliberately no `currentPage` field here. It used to exist, resolved in
   * `PresentationEdit` against the *saved* presentation, which is one save behind the tree the
   * editor is actually showing (#22677).
   */
  selectedKey?: string
  sidebarPage?: string
  isDirty?: boolean
  isSaving?: boolean
  isEditable?: boolean
  setIsDirty?: (value: boolean) => void
  setIsSaving?: (value: boolean) => void
  preview?: PresentationPreview
  setPreview?: (preview: PresentationPreview) => void
  /**
   * How much the canvas is scaled by (#23729) — a factor, or `'fit'` to keep it sized to whatever
   * room the fixed chrome leaves. Session state, never saved: it's how the user is looking at the
   * presentation, not part of it. The canvas keeps its full logical width at every zoom, so this
   * changes nothing about what the customer is shown.
   */
  zoom?: PresentationZoom
  setZoom?: (zoom: PresentationZoom) => void
  /**
   * `zoom` as the factor actually rendered — `'fit'` already resolved against the room available
   * this render. Only `PresentationEdit` knows the gutter widths to resolve it, and the zoom pill
   * needs a number to step from, so the resolved value is shared rather than recomputed.
   *
   * Deliberately absent from `PresentationContextInitValue`: outside the builder there is no canvas
   * to resolve `'fit'` against, so any default here would be a number nobody computed. Consumers
   * supply their own fallback (`PresentationDeviceFloat` uses `?? 1`), which keeps the "no editor
   * mounted" case visible instead of dressing it up as a real measurement.
   */
  resolvedZoom?: number
  /**
   * The key of the section that was in view on the editor canvas when preview was switched on, so
   * the preview iframe opens where the user was working instead of at the top of page 1 (#23716).
   *
   * A section key, never a page key: only the active page's sections are in the DOM (#21990), so
   * the section identifies the page too, and `PresentationImbedPreview` recovers it with
   * `resolvePageForKey` against the live tree. Storing a page key as well would add a second
   * value that can disagree with the first.
   *
   * Captured on the edit → preview transition only. While preview is already showing, the canvas
   * is unmounted and a fresh read would find nothing.
   */
  previewAnchorKey?: string
  /**
   * True when the editor canvas was scrolled fully to the top when preview was switched on, or the
   * preview was when the device changed (ENG-2004/#23984). The preview then stays at the top instead
   * of aligning `previewAnchorKey` under the header. The key is still kept: it identifies the page.
   */
  previewAnchorAtTop?: boolean
  /**
   * The preview iframe's own `document`/`window`, kept live while preview is mounted so the
   * preview → editor anchor capture (ENG-2004/#23984) can read it at the moment "back to editor" is
   * clicked. `PresentationPreview`/`PresentationImbedPreview` and the buttons that call `setPreview`
   * are siblings under `PresentationEdit`, not ancestor/descendant, so this has to travel through
   * context rather than as a prop — the same reason `catalogDrag` does.
   */
  previewFrameRef?: React.MutableRefObject<{ doc: Document; win: Window } | null>
  /**
   * A real block's `key`, or `GLOBAL_FOOTER_BLOCK_KEY` (`PresentationNext/util/presentationEditorDom`)
   * when the global footer (#22211) is selected instead — the footer isn't a real block (no tree row,
   * unreachable via `findBlock`), but sharing this one field means selecting a block clears the
   * footer's selection for free, and vice versa, with no separate cross-clearing logic needed.
   */
  focusedBlock?: string
  setFocusedBlock?: (blockKey: string) => void
  /**
   * The inspector's drill-in target within the focused block — a media/logo/text `key`
   * (`media::<blockPrefix>.<fieldSuffix>` / `logo::<blockPrefix>.<fieldSuffix>` /
   * `text::<blockPrefix>.<fieldSuffix>`), or `null` when the inspector should show the focused
   * block's own options. Cleared whenever `focusedBlock` changes.
   *
   * `type: 'text'` carries the live TipTap `editor` instance (set on that field's own `onFocus`,
   * cleared on its `onBlur` — not a click handler, since a block can hold more than one text field
   * and only caret focus disambiguates which one is live) plus its `templateTags`/`templateContext`,
   * so the inspector's Format-tab toolbar (#22203) can bind directly to it.
   */
  focusedElement?: FocusedElement | null
  setFocusedElement?: (element: FocusedElement | null) => void
  /**
   * Set while a catalog tile (Add panel Sections/Blocks) is being natively dragged, so the canvas
   * (a sibling component, not a descendant of the Add panel) knows to show its zone rails and which
   * kind they should accept. `null` when no catalog drag is in progress — reorder drags on the
   * canvas itself (`@hello-pangea/dnd`) don't touch this; they carry their own kind on the DropResult.
   */
  catalogDrag?: CatalogDrag | null
  setCatalogDrag?: (drag: CatalogDrag | null) => void
  presentationTheme?: Theme
  presentationStyle?: PresentationStyle
  setPresentationStyle?: (presentationStyle: PresentationStyle) => void
  presentationLibrary?: {
    addFilesToLibrary: (args: { files: QuoteFile[] }) => void
    library: PresentationLibrary
    saveLibrary: (args: { library: PresentationLibrary }) => void
  }
  addPages?: boolean
  setAddPages?: (addPages: boolean) => void
  autosaveDisabled?: boolean

  /** Selects a tree node (page, section, or block) by key — sets {@link selectedKey}. */
  onSelectKey?: (selectedKey: string) => any
  onSidebarPageChange?: (sidebarPage: string) => any
  useCart?: boolean
  sidebarWidth?: number
  /**
   * Requests a deferred navigation away from the builder once a manual save finishes. Owned by
   * PresentationEdit's reducer rather than component-local state: a real save changes
   * `dates.updated`, which remounts `<FormikUndo>` — and everything inside it, including whichever
   * component calls this — right around when a save completes (#22889/ENG-1498). Setting it here,
   * above `<FormikUndo>`, survives that remount; local state on the caller does not.
   */
  setSaveExitPending?: (value: boolean) => void
}

export interface FocusedElement {
  type: 'media' | 'logo' | 'text'
  key: string
  /** Only set for `type: 'text'` — the live TipTap editor instance for the focused field. */
  editor?: Editor
  templateTags?: TemplateTag[]
  templateContext?: Context
  /**
   * Only set for `type: 'media'` — whether this field allows switching to a YouTube video (some
   * fields, e.g. Logo/Card/SliderImage, pass `youtube={false}` to `InlineEditableMedia` to opt out).
   * Carried on the focus state itself since the inspector renders this field's options independent
   * of the `InlineEditableMedia` instance that set focus.
   */
  allowYoutube?: boolean
}

const typographyNullStyles = {
  color: '',
  fontFamily: '',
  fontSize: '',
  fontStyle: '',
  fontWeight: null,
  letterSpacing: '',
  textDecoration: '',
  textTransform: 'none' as TypographyStyleOptions['textTransform'],
  lineHeight: ''
}

export const PresentationContextInitValue: PresentationContextValue = {
  isDirty: false,
  isSaving: false,
  isEditable: false,
  preview: 'none',
  zoom: 'fit',
  focusedBlock: '',
  focusedElement: null,
  catalogDrag: null,
  presentationStyle: {
    accentColor: '#000000',
    backgroundColor: '#FFFFFF',
    buttonColor: '#000000',
    textColor: '#000000',
    fontFamily: 'roboto, sans-serif',
    heading1: typographyNullStyles,
    heading2: typographyNullStyles,
    heading3: typographyNullStyles,
    body: typographyNullStyles
  },
  useCart: false,
  addPages: false,

  setAddPages: () => {},
  setIsDirty: () => {},
  setIsSaving: () => {},
  setPreview: () => {},
  setFocusedBlock: () => {},
  setFocusedElement: () => {},
  setCatalogDrag: () => {},
  onSelectKey: () => {},
  onSidebarPageChange: () => {},
  setSaveExitPending: () => {}
}

export const PresentationContext = React.createContext<PresentationContextValue>(PresentationContextInitValue)
